import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { sendFeedback } from "@features/feedback/helpers";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import useAuthUser from "@hooks/useAuthUser";
import { trackCustomEvent } from "analytics";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const useDelete = () => {
	const [showDeleteFeedbackDialog, setShowDeleteFeedbackDialog] =
		useState(false);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] =
		useState(false);
	const [checkboxStates, setCheckboxStates] = useState<
		Record<string, "checked" | "unchecked"> | undefined
	>(undefined);

	const [feedback, setFeedback] = useState("");

	const { t } = useTranslation();

	const { username, email } = useAuthUser();

	const appName = useAppStore((store) => store.app.name);

	const options = t("dialog.delete.options")
		.split("|")
		.map((option) => option.trim());

	const selectedCheckboxes = checkboxStates
		? Object.keys(checkboxStates).filter(
				(key) => checkboxStates[key] === "checked",
			)
		: [];
	const selectedReasons = selectedCheckboxes
		.map(
			(key: string) =>
				`- ${
					// biome-ignore lint/suspicious/noExplicitAny: <explanation>
					(options as any)[key]
				}`,
		)
		.join("\n");
	const toggleShowDeleteFeedbackDialog = () => {
		setShowDeleteFeedbackDialog((prev) => !prev);
	};

	const toggleShowDeleteConfirmationDialog = () => {
		setShowDeleteConfirmationDialog((prev) => !prev);
	};

	const handleChangeCheckboxState = (index: string) => {
		const newSelection =
			checkboxStates?.[index] === "checked" ? "unchecked" : "checked";
		const newSelections: Record<string, "checked" | "unchecked"> = {
			...(checkboxStates || {}),
			[index]: newSelection,
		};
		setCheckboxStates(newSelections);
	};

	const handleSetFeedback = (text: string) => {
		setFeedback(text);
	};

	const {
		currentExamContext: {
			projectId,
			organizationId,
			studyPackProductId: productId,
		},
	} = useExamContext();

	const handleSendFeedback = () => {
		trackCustomEvent({
			eventName: events.deleteFeedbackSent,
			source: "Delete_Dialog",
		});
		if (selectedReasons && feedback) {
			const params = {
				userName: username || "",
				email: email || "",
				appName,
				feedback: `${selectedReasons} \n\n ${feedback}`,
				type: "Deletion" as const,
				view: "settings",
				projectId,
				organizationId,
				productId,
				appLanguage: i18n.language,
			};
			sendFeedback(params);
		}
	};

	return {
		showDeleteFeedbackDialog,
		showDeleteConfirmationDialog,
		toggleShowDeleteFeedbackDialog,
		toggleShowDeleteConfirmationDialog,
		selectedReasons,
		handleChangeCheckboxState,
		feedback,
		handleSetFeedback,
		handleSendFeedback,
		checkboxStates,
	};
};
